import { Injectable } from '@angular/core';
import { AbstractUndoManager } from '../../../../../../cross-team-shared/state/abstract-undo-manager';
import { ObjectWith } from '../../../../../../cross-team-shared/types/misc';
import { isShapeLayer } from '../../../../../../outputs/annotation/annotation-shared/state/layers/layer-utils';
import { AbstractAnnotationStore } from '../../../../../../outputs/annotation/annotation-shared/state/root/abstract-annotation-store';
import {
  AnnotationState,
  getFocusedShapeIndex,
} from '../../../../../../outputs/annotation/annotation-shared/state/root/annotation-state';
import { FusionManager } from '../../fusion/fusion-manager';
import { ShapeSelectionService } from '../../utils/shape-selection.service';
import { AbstractLayerState } from '../layers/abstract-layer-state';

interface UndoState {
  activeLayerIndex: number;
  layers: AbstractLayerState[];
  frameNumber: number;
}

/**
 * AnnotationStateUndoService is responsible for updating the AnnotationState
 * after an undo or a redo and setting that state to the AnnotationStore
 */
@Injectable()
export class AnnotationStateUndoService extends AbstractUndoManager<
  AnnotationState,
  UndoState
> {
  constructor(
    private rootStore: AbstractAnnotationStore,
    private fusionManager: FusionManager,
    private selectionService: ShapeSelectionService,
  ) {
    super(rootStore);
  }

  protected override applyUndoState(
    stateToApply: UndoState,
    undoOrRedo: 'undo' | 'redo',
    stateToOverwrite: UndoState,
  ) {
    const newerState = undoOrRedo === 'undo' ? stateToOverwrite : stateToApply;

    this.rootStore.batch(() => {
      this.store.assign({
        activeLayerIndex: newerState.activeLayerIndex,
        layers: stateToApply.layers,
      });

      this.setTransientLayerStates(
        newerState,
        stateToOverwrite,
        // @ts-ignore -- fix me
        this.selectionService.getSelectionForOutput(),
      );
    });

    /**
     * TODO: remove this. we should simply have a subscription listening to the frameNumber in the fusionStore and doing the scrubbing, and frameNumber needs to be removed from the annotation store.
     */
    this.fusionManager.scrubTo(newerState.frameNumber);
  }

  protected override extractUndoState(state: AnnotationState): UndoState {
    return {
      activeLayerIndex: state.activeLayerIndex,
      // TODO: verify if we really need to clone the layers
      layers: state.layers.map((layer) => ({ ...layer })),
      // fix me if you change this code
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
      frameNumber: state.frameNumber ?? 0,
    };
  }

  protected override hasTrackedChanges() {
    return false;
  }

  private setTransientLayerStates(
    newerState: UndoState,
    stateToOverwrite: UndoState,
    selection: ObjectWith<true>,
  ) {
    newerState.layers.forEach((layer, index) => {
      this.store('layers')(index)('transient').set(
        stateToOverwrite.layers[index].transient,
      );
      if (isShapeLayer(layer)) {
        this.store('layers')(index)('transient').assign(layer.transient);

        const shouldCloseTagEditor =
          !layer.transient.tagEditorHidden &&
          getFocusedShapeIndex(selection) === undefined;

        if (shouldCloseTagEditor) {
          this.store('layers')(index)('transient').assign({
            tagEditorHidden: shouldCloseTagEditor,
            shapeListHidden: !shouldCloseTagEditor,
          });
        }
      }
    });
  }
}
